import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { slugifyCategory, formatCatalogDisplayName } from '../utils/categoryMenuTree';
import './MobileCategorySheet.css';

const MobileCategorySheet = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { categoriesForNav, categoryMenuTreeByCategory } = useData();
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [activeGroupId, setActiveGroupId] = useState(null);

  const categories = useMemo(
    () => (categoriesForNav || []).map((cat) => ({
      id: String(cat._id || cat.id),
      name: cat.name,
      slug: cat.slug || slugifyCategory(cat.name),
    })),
    [categoriesForNav]
  );

  const activeCategory = categories.find((c) => c.id === activeCategoryId) || null;
  const panel2Items = activeCategoryId
    ? categoryMenuTreeByCategory?.[activeCategoryId] || []
    : [];
  const activeGroup = panel2Items.find((g) => String(g.id) === String(activeGroupId)) || null;
  const panel3Items = activeGroup?.isGroup ? activeGroup.children || [] : [];

  const resetDrill = useCallback(() => {
    setActiveCategoryId(null);
    setActiveGroupId(null);
  }, []);

  const handleClose = useCallback(() => {
    resetDrill();
    onClose?.();
  }, [onClose, resetDrill]);

  useEffect(() => {
    if (!open) {
      resetDrill();
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, resetDrill]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (activeGroupId) {
        setActiveGroupId(null);
      } else if (activeCategoryId) {
        setActiveCategoryId(null);
      } else {
        handleClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, activeCategoryId, activeGroupId, handleClose]);

  const goToSubcategory = (category, sub) => {
    const catSlug = category.slug || slugifyCategory(category.name);
    const subSlug = sub.slug || slugifyCategory(sub.name);
    navigate(`/category/${catSlug}/${subSlug}`);
    handleClose();
  };

  const headerTitle = activeGroup
    ? formatCatalogDisplayName(activeGroup.name)
    : activeCategory
      ? formatCatalogDisplayName(activeCategory.name)
      : 'Categories';

  const showBack = Boolean(activeCategoryId);

  const handleBack = () => {
    if (activeGroupId) {
      setActiveGroupId(null);
      return;
    }
    if (activeCategoryId) {
      setActiveCategoryId(null);
    }
  };

  if (!open) return null;

  let rows = [];

  if (!activeCategoryId) {
    rows = categories.map((cat) => {
      const tree = categoryMenuTreeByCategory?.[cat.id] || [];
      return {
        key: cat.id,
        label: formatCatalogDisplayName(cat.name),
        hasChildren: tree.length > 0,
        onClick: () => {
          if (tree.length > 0) {
            setActiveCategoryId(cat.id);
            setActiveGroupId(null);
          } else {
            navigate(`/category/${cat.slug || slugifyCategory(cat.name)}`);
            handleClose();
          }
        },
      };
    });
  } else if (!activeGroupId) {
    rows = panel2Items.map((item) => {
      const hasChildren = item.isGroup && (item.children?.length > 0);
      return {
        key: item.id,
        label: formatCatalogDisplayName(item.name),
        hasChildren,
        disabled: false,
        onClick: () => {
          if (hasChildren) {
            setActiveGroupId(String(item.id));
            return;
          }
          goToSubcategory(activeCategory, item);
        },
      };
    });
  } else {
    rows = panel3Items.map((leaf) => ({
      key: leaf.id,
      label: formatCatalogDisplayName(leaf.name),
      hasChildren: false,
      onClick: () => goToSubcategory(activeCategory, leaf),
    }));
  }

  return createPortal(
    <div className="mobile-category-sheet-root" role="presentation">
      <button
        type="button"
        className="mobile-category-sheet-backdrop"
        aria-label="Close categories"
        onClick={handleClose}
      />
      <div
        className="mobile-category-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={headerTitle}
      >
        <header className="mobile-category-sheet-header">
          <div className="mobile-category-sheet-header-left">
            <button
              type="button"
              className={`mobile-category-sheet-back${showBack ? '' : ' mobile-category-sheet-back--hidden'}`}
              aria-label="Go back"
              onClick={handleBack}
            >
              ←
            </button>
            <h2 className="mobile-category-sheet-title">{headerTitle}</h2>
          </div>
          <button
            type="button"
            className="mobile-category-sheet-close"
            aria-label="Close categories"
            onClick={handleClose}
          >
            ×
          </button>
        </header>
        <div className="mobile-category-sheet-body">
          {rows.length === 0 ? (
            <p className="mobile-category-sheet-empty">No subcategories available.</p>
          ) : (
            rows.map((row) => (
              <button
                key={row.key}
                type="button"
                className={`mobile-category-sheet-row${row.hasChildren ? '' : ' mobile-category-sheet-row--leaf'}`}
                disabled={row.disabled}
                onClick={row.onClick}
              >
                <span>{row.label}</span>
                {row.hasChildren ? <span className="mobile-category-sheet-chevron" aria-hidden>›</span> : null}
              </button>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MobileCategorySheet;
